"use client";

import type { LanguageMetadata } from "@mw/shared/languagesAndAccents";

import { useEffect, useState } from "react";
// TODO: migrate to react aria alternative
import { SelectTrigger } from "@radix-ui/react-select";
import { convAILanguages } from "@mw/shared/languagesAndAccents";
import { LanguageSelector } from "@ui/components/core/LanguageSelector";
import Orb2 from "@ui/components/demos/ConvAIHero/Orb/Orb2";
import DynamicFlag from "@ui/components/languages/DynamicFlag";
import CallCancel from "@ui/icons/call-cancel.svg";
import Mic from "@ui/icons/mic.svg";
import MicOff from "@ui/icons/mic-off.svg";
import { formatTime } from "@ui/lib/time";
import { Loader2Icon, Maximize2Icon, Minimize2Icon, PhoneIcon } from "lucide-react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { cva } from "class-variance-authority";

import { useCallManager } from "./CallManager";
import clsx from "clsx";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

const containerVariants = cva("pointer-events-none fixed bottom-16 z-50 flex", {
  variants: {
    position: {
      center: "left-0 w-full justify-center",
      right: "right-16 justify-end",
    },
  },
  defaultVariants: {
    position: "center",
  },
});

function MinimizedView({
  elapsed,
  onExpand,
  position = "center",
}: {
  elapsed: number;
  onExpand: () => void;
  position?: "center" | "right";
}) {
  return (
    <div className={containerVariants({ position })}>
      <m.div
        className='pointer-events-auto flex items-center justify-between rounded-[30px] bg-[#F5F2EF]/80 p-12 pl-[14px] pr-[12px] backdrop-blur-lg'
        layoutId='floating-agent'
        style={{
          boxShadow: "0px 6px 16px 0px rgba(78, 50, 23, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.05)",
          height: "52px",
          minWidth: "180px",
          borderRadius: "30px",
        }}
        transition={{
          layout: { type: "spring", stiffness: 400, damping: 40, duration: 0.6 },
        }}
      >
        <div className='flex items-center gap-12'>
          <m.div className='h-28 w-28' layoutId='orb'>
            <Orb2 size={28} />
          </m.div>
          <m.span
            animate={{ opacity: 1 }}
            className='f-description-03 tabular-nums text-light'
            initial={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeInOut",
              delay: 0.2,
            }}
          >
            {formatTime(elapsed)}
          </m.span>
        </div>
        <m.button
          aria-label='Expand'
          className='flex h-32 w-32 items-center justify-center rounded-[30px] bg-overlay-dark transition-colors duration-300 ease-in-out hover:bg-overlay-darker'
          layoutId='minimize-button'
          onClick={onExpand}
        >
          <Maximize2Icon size={16} />
        </m.button>
      </m.div>
    </div>
  );
}

export function ExpandedView({
  elapsed,
  onMinimize,
  onEndCall,
  onStartCall,
  getInputVolume,
  getOutputVolume,
  onToggleMute,
  muted,
  language,
  onLanguageChange,
  variant = "floating",
  status,
  agentName,
  agentTitle,
  agentImageUrl,
  position = "center",
  isStarting = false,
}: {
  agentName?: string;
  agentTitle?: string;
  agentImageUrl?: string;
  elapsed: number;
  getInputVolume?: () => number;
  getOutputVolume?: () => number;
  isStarting?: boolean;
  language: LanguageMetadata;
  muted: boolean;
  onEndCall: () => void;
  onLanguageChange: (language: string) => void;
  onMinimize?: () => void;
  onStartCall?: () => void;
  onToggleMute: () => void;
  status: string;
  variant?: "floating" | "inline";
  position?: "center" | "right";
}) {
  const t = useTranslations("ClientKeys.conversational_ai_page");

  const containerContent = (
    <>
      <div className='mb-32 flex w-full items-center justify-center'>
        <m.span className='f-ui-02 tabular-nums text-light'>{formatTime(elapsed)}</m.span>
        {variant === "floating" && onMinimize && (
          <m.button
            animate={{ opacity: 1 }}
            aria-label='Minimize'
            className='absolute right-8 top-8 flex h-40 w-40 items-center justify-center rounded-full bg-overlay-dark transition-colors duration-300 ease-in-out hover:bg-overlay-darker'
            initial={{ opacity: 0 }}
            onClick={onMinimize}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Minimize2Icon size={16} />
          </m.button>
        )}
      </div>
      <div className='flex flex-col items-center'>
        <m.div className='mb-12 h-100 w-100' layoutId={variant === "floating" ? "orb" : undefined}>
          {agentImageUrl ? (
            <img src={agentImageUrl} alt={agentName || "Agent"} className='h-100 w-100 rounded-full object-cover' />
          ) : (
            <Orb2 getInputVolume={getInputVolume} getOutputVolume={getOutputVolume} size={100} />
          )}
        </m.div>
        <div className='mb-40 flex flex-col items-center'>
          <span className='f-description-01'>{agentName || t("floating_agent.agent_name")}</span>
          <span className='f-paragraph-03 text-overlay-darker-plus-2'>
            {agentTitle || t("floating_agent.agent_title")}
          </span>
        </div>
      </div>
      <div className='mb-16 flex w-full items-center justify-center gap-8'>
        <LanguageSelector
          customSelectTrigger={({ language }) => (
            <SelectTrigger
              className='flex h-48 w-48 items-center justify-center rounded-full bg-overlay-dark transition-colors duration-300 ease-in-out hover:bg-overlay-darker'
              title={t("floating_agent.change_language")}
            >
              <DynamicFlag className='rounded-full' locale={language?.value ?? ""} size={20} />
            </SelectTrigger>
          )}
          language={language}
          setSelectedLanguage={onLanguageChange}
          sortedLanguages={convAILanguages}
        />
        <button
          className='flex h-48 w-48 items-center justify-center rounded-full bg-overlay-dark transition-colors duration-300 ease-in-out hover:bg-overlay-darker disabled:cursor-not-allowed disabled:opacity-50'
          disabled={status !== "connected" && status !== "connecting"}
          onClick={onToggleMute}
          title={muted ? t("floating_agent.unmute") : t("floating_agent.mute")}
        >
          {muted ? <MicOff className='h-20 w-20 text-default' /> : <Mic className='h-20 w-20 text-default' />}
        </button>
        {status === "connected" || status === "connecting" || isStarting || !onStartCall ? (
          <button
            className='flex h-48 w-48 items-center justify-center rounded-full bg-[#FF6554] text-inverse transition-colors duration-300 ease-in-out hover:bg-[#E94735] disabled:cursor-not-allowed disabled:opacity-50'
            onClick={onEndCall}
            disabled={status === "connecting" || isStarting}
            title='End call'
          >
            {status === "connecting" || isStarting ? (
              <Loader2Icon className='h-20 w-20 animate-spin text-inverse' />
            ) : (
              <CallCancel className='h-20 w-20 text-inverse' />
            )}
          </button>
        ) : (
          <button
            className={clsx(
              "flex h-48 w-48 items-center justify-center rounded-full transition-colors duration-300 ease-in-out",
              variant === "inline" ? "bg-overlay-darkest-plus hover:bg-inverse" : "hover:bg-green-darker bg-green-dark",
            )}
            onClick={onStartCall}
            title='Start call'
          >
            <PhoneIcon className='h-20 w-20 text-inverse' fill='currentColor' />
          </button>
        )}
      </div>
      <p className='f-paragraph-04 w-full text-balance text-center text-overlay-darker-plus-2'>
        {t("floating_agent.end_call_description")}
      </p>
    </>
  );

  if (variant === "inline") {
    return (
      <div className='flex w-full items-center justify-center'>
        <div
          className='flex w-full max-w-[400px] flex-col items-center gap-8 rounded-[30px] bg-default p-32 pt-16 backdrop-blur-xl'
          style={{
            boxShadow: "0px 6px 16px 0px rgba(78, 50, 23, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.1)",
            minHeight: "440px",
            borderRadius: "30px",
          }}
        >
          {containerContent}
        </div>
      </div>
    );
  }

  return (
    <div className={containerVariants({ position })}>
      <m.div
        className={clsx(
          "pointer-events-auto flex flex-col items-center gap-8 rounded-[30px] p-32 pt-16 backdrop-blur-xl",
          position === "right" ? "w-[80vw] max-w-[400px] md:w-full" : "w-full max-w-[400px]",
        )}
        layoutId='floating-agent'
        style={{
          backgroundColor: "rgba(245, 242, 239, 0.8)",
          boxShadow: "0px 6px 16px 0px rgba(78, 50, 23, 0.04), 0 0 0 1px rgba(0, 0, 0, 0.1)",
          minHeight: "440px",
          borderRadius: "30px",
        }}
        transition={{
          layout: { type: "spring", stiffness: 400, damping: 40, duration: 0.6 },
        }}
      >
        {containerContent}
      </m.div>
    </div>
  );
}

export default function CallManagerUI({
  onEndCall,
  agentId,
  agentName,
  agentTitle,
  agentImageUrl,
  position = "center",
  variant = "floating",
  autoStart = false,
}: {
  agentId: string;
  agentName?: string;
  agentTitle?: string;
  agentImageUrl?: string;
  onEndCall: () => void;
  position?: "center" | "right";
  variant?: string;
  autoStart?: boolean;
}) {
  const [state, actions] = useCallManager(agentId, { variant, autoStart });
  const [isStarting, setIsStarting] = useState(autoStart);

  // Reset isStarting when call connects
  useEffect(() => {
    if (state.status === "connected") {
      setIsStarting(false);
    }
  }, [state.status]);

  // Handle start call with loading state
  const handleStartCall = async () => {
    setIsStarting(true);
    await actions.startCall();
    setIsStarting(false);
  };

  // Handle end call with parent callback
  const handleEndCall = async () => {
    await actions.endCall();
    onEndCall();
  };

  // Handle language change
  const handleLanguageChange = (languageValue: string) => {
    const newLanguage = convAILanguages.find(l => l.value === languageValue)!;
    actions.setLanguage(newLanguage);
  };

  if (state.isMinimized) {
    return (
      <MinimizedView
        elapsed={state.elapsed}
        onExpand={() => {
          actions.setIsMinimized(false);
          captureEvent(EventName.marketing_site_convai_agent_expanded);
        }}
        position={position}
      />
    );
  }

  return (
    <ExpandedView
      agentName={agentName}
      agentTitle={agentTitle}
      agentImageUrl={agentImageUrl}
      elapsed={state.elapsed}
      getInputVolume={state.getInputVolume}
      getOutputVolume={state.getOutputVolume}
      isStarting={isStarting}
      language={state.language}
      muted={state.muted}
      onEndCall={handleEndCall}
      onLanguageChange={handleLanguageChange}
      onMinimize={() => {
        actions.setIsMinimized(true);
        captureEvent(EventName.marketing_site_convai_agent_collapsed);
      }}
      onStartCall={handleStartCall}
      onToggleMute={() => actions.setMuted(!state.muted)}
      status={state.status}
      position={position}
    />
  );
}
